import { fromEvent, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Platform } from '@angular/cdk/platform';
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

interface IFullscreenAPI {
  enabled: string;
  element: string;
  request: string;
  exit: string;
  onchange: string;
  onerror: string;
}

const snFullscreenBrowserAPIs: { [browser: string]: IFullscreenAPI } = {
  w3: {
    enabled: 'fullscreenEnabled',
    element: 'fullscreenElement',
    request: 'requestFullscreen',
    exit: 'exitFullscreen',
    onchange: 'fullscreenchange',
    onerror: 'fullscreenerror',
  },
  newWebkit: {
    enabled: 'webkitFullscreenEnabled',
    element: 'webkitFullscreenElement',
    request: 'webkitRequestFullscreen',
    exit: 'webkitExitFullscreen',
    onchange: 'webkitfullscreenchange',
    onerror: 'webkitfullscreenerror',
  },
  oldWebkit: {
    enabled: 'webkitIsFullScreen',
    element: 'webkitCurrentFullScreenElement',
    request: 'webkitRequestFullScreen',
    exit: 'webkitCancelFullScreen',
    onchange: 'webkitfullscreenchange',
    onerror: 'webkitfullscreenerror',
  },
  moz: {
    enabled: 'mozFullScreen',
    element: 'mozFullScreenElement',
    request: 'mozRequestFullScreen',
    exit: 'mozCancelFullScreen',
    onchange: 'mozfullscreenchange',
    onerror: 'mozfullscreenerror',
  },
  ios: {
    enabled: 'webkitFullscreenEnabled',
    element: 'webkitFullscreenElement',
    request: 'webkitEnterFullscreen',
    exit: 'webkitExitFullscreen',
    onchange: 'webkitfullscreenchange',
    onerror: 'webkitfullscreenerror',
  },
  ms: {
    enabled: 'msFullscreenEnabled',
    element: 'msFullscreenElement',
    request: 'msRequestFullscreen',
    exit: 'msExitFullscreen',
    onchange: 'MSFullscreenChange',
    onerror: 'MSFullscreenError',
  },
};

@Injectable({ providedIn: 'root' })
export class SnFullscreenService {
  public readonly API: IFullscreenAPI;

  private readonly _change$ = new Subject<boolean>();
  private readonly _error$ = new Subject<Event>();

  constructor(
    private readonly _platform: Platform,
    @Inject(DOCUMENT) private readonly _document: Document,
  ) {
    for (const browser in snFullscreenBrowserAPIs) {
      const browserAPI = snFullscreenBrowserAPIs[browser];

      if (browserAPI.enabled in document) {
        this.API = browserAPI;
        break;
      }
    }

    if (this._platform.IOS) {
      this.API = snFullscreenBrowserAPIs.ios;
    }

    fromEvent(this._document, this.API.onchange).subscribe(() => this._change$.next(this.enabled));

    fromEvent(this._document, this.API.onerror).subscribe((event) => this._error$.next(event));
  }

  get enabled(): boolean {
    return !!this._document[this.API.element];
  }

  get change$(): Observable<boolean> {
    return this._change$.asObservable();
  }
  get error$(): Observable<Event> {
    return this._error$.asObservable();
  }

  public request(element: HTMLElement): Observable<void> {
    return new Observable((observer) => {
      if (this.API.request in element) {
        element[this.API.request]();
      } else {
        observer.next();
        element.classList.add('fullscreen');
      }

      const changeSubscription = this._change$
        .pipe(takeUntil(this._error$))
        .subscribe((enabled) => {
          if (enabled) {
            observer.next();
          } else {
            observer.complete();
            changeSubscription.unsubscribe();
          }
        });
    });
  }

  public exit(element: HTMLElement): void {
    if (this.API.exit in this._document) {
      this._document[this.API.exit]();
    } else {
      this._change$.next(false);
      element.classList.remove('fullscreen');
    }
  }
}
